import { convertFromRaw, RawDraftContentState } from 'draft-js';
import { isEqual, isNumber, isString } from 'lodash';
import { CellData } from '../../../../../../common/table/TableTypes';
import { cellCanBeDraftFormatted, formatCellAsType } from '../../../../../../common/table/utils/tableCellTypeUtils';
import doesEditorJsonHaveText from '../../../../../../common/tiptap/utils/jsonContentUtils/doesEditorJsonHaveText';
import rawGetText from '../../../../../../common/utils/editor/rawUtils/rawGetText';
import { getCellLevelTextStyles } from './tableCellStyleUtils';
import { cellIsReadOnly } from './tableCellTypeUtils';

/**
 * Returns whether incoming editor content should be saved as a populated value.
 */
const isCellContentPopulated = (content: RawDraftContentState | string | number | null | undefined): boolean => {
    if (isString(content)) return content !== '';
    if (isNumber(content)) return true;

    return doesEditorJsonHaveText(content);
};

/**
 * Builds the next persisted cell model from a raw editor update.
 */
export const createNewCellData = (
    prevCellData: CellData,
    newCellValue: string | null,
    locale: string,
    currencyPreference: string,
    tableEditorDraftContent?: RawDraftContentState,
): CellData => {
    let newCellData = { ...prevCellData, value: newCellValue };
    delete newCellData.draftContent;
    delete newCellData.resultValue;

    // If cell has no content then return empty cell data with a consistent format
    const hasContent = newCellValue !== null && isCellContentPopulated(tableEditorDraftContent || newCellValue);
    if (!hasContent) {
        newCellData.value = null;

        if (cellIsReadOnly(newCellData)) {
            delete newCellData.type;
        }

        return newCellData;
    }

    newCellData = formatCellAsType(newCellData, prevCellData.value, locale, currencyPreference);

    if (!cellCanBeDraftFormatted(newCellData)) return newCellData;
    if (cellIsReadOnly(newCellData)) return newCellData;

    if (!tableEditorDraftContent) return newCellData;

    newCellData.draftContent = tableEditorDraftContent;
    newCellData.value = rawGetText(tableEditorDraftContent);

    const cellLevelTextStyles = getCellLevelTextStyles(convertFromRaw(newCellData.draftContent));
    const stylesMatch = isEqual(cellLevelTextStyles.sort(), newCellData.textStyle?.sort() || []);

    if (!stylesMatch || !cellCanBeDraftFormatted(newCellData)) {
        newCellData.textStyle = cellLevelTextStyles;
    }

    return newCellData;
};
